import { useEffect, useRef } from 'react';

import { INITDATA_WARN_AGE_SEC, getInitDataAgeSec, shouldWarnAge } from '../lib/authDate.js';
import { useT } from '../hooks/useT.js';
import { useToast } from './ToastProvider.js';

/**
 * Раннее предупреждение о скором истечении пропуска Telegram — work-038 (§8).
 *
 * Ничего не рисует (возвращает `null`) — только один раз за запуск показывает
 * тост «пропуск скоро истечёт», чтобы человек перезапустил приложение сам,
 * а не узнал о протухшем пропуске в момент отправки набранного текста.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ КОМПОНЕНТ, А НЕ КОД В `App`. Дерево провайдеров рендерит
 * сам `App` (`App.tsx:373-376`: `<AppRoot><LocaleProvider><SetLocaleProvider>
 * <ToastProvider>`), то есть `App` НЕ потомок этих провайдеров: `useToast()`
 * в его теле бросил бы `Error` (`ToastProvider.tsx:53-57`) и уронил бы всё
 * приложение у здорового человека с живым пропуском, а `useT()` молча отдал бы
 * русские строки вместо выбранного языка. Компонент монтируется ВНУТРИ
 * `<ToastProvider>` — здесь оба хука легальны.
 *
 * Возраст проверяется на монтировании И при возврате вкладки в видимое
 * состояние. Проверка на mount обязательна: зафиксированный на проде инцидент
 * (19 июля) — это ДОЛГО ОТКРЫТОЕ приложение, где `visibilitychange` не
 * наступает вовсе, и без mount-проверки блок не покрывал бы собственный
 * мотивирующий случай.
 *
 * Никаких решений о доступе, никаких запросов, никакого поллинга — только
 * текст. Единственная граница доверия — серверный 401 (CANON D1/SP3);
 * клиентская оценка возраста НИКОГДА не гейт. Если возраст неизвестен или
 * неправдоподобен (`getInitDataAgeSec()` → `undefined`, часам не верим) —
 * молчим.
 */
export function SessionAgeWarning(): null {
  const { showError } = useToast();
  const t = useT();

  // Ровно одно предупреждение за запуск приложения (StrictMode-safe).
  const warned = useRef(false);
  // Свежие тост и строки на момент срабатывания: слушатель живёт весь запуск,
  // а язык может приехать с сервера уже после монтирования (`App.tsx:138-144`).
  // Приём тот же, что у `useBackButton` (`lib/nativeUi.ts:39-40`).
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const textRef = useRef(t.session_expiring_soon);
  textRef.current = t.session_expiring_soon;

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    function checkAge(): void {
      if (warned.current) return;
      const ageSec = getInitDataAgeSec();
      if (shouldWarnAge(ageSec)) {
        warned.current = true;
        showErrorRef.current(textRef.current);
        return;
      }
      // Порог ещё не наступил, но возраст известен — ставим ОДИН будильник
      // ровно на момент, когда наступит.
      //
      // Без него блок не покрывал бы собственный мотивирующий случай: в долго
      // открытом приложении (инцидент 19 июля) монтирование случилось, когда
      // пропуск был свежим, а `visibilitychange` не наступает вовсе — человек
      // так и не получил бы предупреждения и узнал бы о протухшем пропуске
      // в момент отправки набранного текста.
      //
      // Это НЕ поллинг (§10 п.5 плана): ровно один отложенный вызов, без
      // повторов и без единого запроса к серверу.
      if (ageSec === undefined) return;
      clearTimeout(timerRef.current);
      const leftMs = (INITDATA_WARN_AGE_SEC - ageSec) * 1000;
      timerRef.current = setTimeout(checkAge, leftMs);
    }

    // Проверка на монтировании.
    checkAge();

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') checkAge();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(timerRef.current);
    };
  }, []);

  return null;
}
